<template>
  <component
    :is="element"
    class="product-prices"
    :class="[
      { 'is-sale': formattedCompareAtPrice },
      { 'is-loading' : state !== 'ready' }
    ]"
  >
    <span
      v-if="formattedCompareAtPrice"
      ref="comparePriceElement"
      class="
        product-prices__container
        product-prices__container--compare
      "
    >
      <span
        class="product-prices__label"
        v-text="comparePriceLabel"
      />

      <span
        class="product-prices__compare-price"
        v-text="formattedCompareAtPrice"
      />
    </span>

    <span
      ref="priceElement"
      class="
        product-prices__container
        product-prices__container--price
      "
    >
      <span
        v-if="formattedCompareAtPrice"
        class="product-prices__label"
        v-text="priceLabel"
      />

      <span
        v-if="formattedPrice"
        class="product-prices__price"
        v-text="formattedPrice"
      />
    </span>
  </component>
</template>

<script>

/**
 * Component: Product Prices
 * ------------------------------------------------------------------------------
 * - Renders a global 'Product Price' template.
 * @param {Number|Boolean} compareAtPrice - Compare at price if it exists.
 * @param {Object} product - Formatted Shopify product object.
 * @param {Number} price - Product price, in cents format.
 * @param {String} type - Type of heading element to use.
 * @param {String} state - Loading state of the currency. Usually set to 'loading'
 * whilst waiting for locale pinia store to set.
 * - Setting a loading state hides the element and prevents any visual GBP
 * to currency conversion.
 */
import { formatLocalizedPrice } from '~/helpers/format'

export default {
  name: 'ProductPrices',

  props: {
    element: {
      type: String,
      default: 'p',
    },
    product: {
      type: [Boolean, Object],
      default: false,
    },
  },

  data() {
    return {
      compareAtPrice: 0,
      price: 0,
      formattedPrice: '',
      formattedCompareAtPrice: '',
      formatted: {
        price: '',
        compareAtPrice: '',
      },
      state: 'loading',
    }
  },

  computed: {

    /**
     * Return a label for the 'Compare At' price.
     * @returns {String}
     */
    comparePriceLabel() {
      return mint.strings.products.wasPrice
    },

    /**
     * Return a label for the price.
     * @returns {String}
     */
    priceLabel() {
      return mint.strings.products.nowPrice
    },
  },

  watch: {

    /**
     * Watch the product sale price.
     */
    'product.compareAtPrice.amount': {
      handler() {
        this.setPrices()
      },
      immediate: true,
    },

    /**
     * Watch the product price.
     */
    'product.price.amount': {
      handler() {
        this.setPrices()
      },
      immediate: true,
    },
  },

  mounted() {
    this.setPrices()
  },

  methods: {

    /**
     * Set the regular and compare-at prices.
     */
    setPrices() {
      this.state = 'loading'

      if (!this.product?.handle) {
        return
      }

      this.price = this.product.price.amount
      this.compareAtPrice = this.product.compareAtPrice.amount

      this.formattedCompareAtPrice = this.compareAtPrice && this.price < this.compareAtPrice
        ? formatLocalizedPrice(this.compareAtPrice)
        : ''

      this.formattedPrice = this.price
        ? formatLocalizedPrice(this.price)
        : ''

      this.state = 'ready'
    },
  },
}
</script>
